import { cloneDeep } from 'lodash';

import type { InjectedRouter } from 'js/lib/connectToRouter';

import { NUMBER_OF_RESULTS_PER_PAGE, SEARCH_STATE_URL_PARAMS } from 'bundles/search-common/constants';
import {
  IS_PART_OF_COURSERA_PLUS,
  SEARCH_FACET_PARAMS_NOT_SUPPORTED_BY_ALGOLIA,
} from 'bundles/search-common/constants/filter';
import type {
  SearchConfig,
  SearchQuery,
  SearchRequest,
  SearchRequests,
  SearchResultAugments,
  SearchResultRaw,
  SearchResults,
  SearchState,
  SearchURLParameters,
} from 'bundles/search-common/providers/searchTypes';

function updateUrl(router: InjectedRouter, newPath: string, shouldReplaceRoute: boolean) {
  if (shouldReplaceRoute) {
    router.replace(newPath);
  } else {
    router.push(newPath);
  }
}

function filtersToKeyValuesMap(filters: string[]) {
  let obj: Record<string, string> = {};
  filters.forEach((filter) => {
    if (filter) {
      const [filterName, ...values] = filter.split(':');
      const value = values.join(':').replace(/^:+/, ''); // 1. join if values contains colon 2. remove colon at the start of string
      if (filterName) {
        obj = { ...obj, [filterName]: value };
      }
    }
  });
  return obj;
}

function syncSearchStateToUrl(router: InjectedRouter, searchState: SearchState, shouldReplaceRoute: boolean) {
  const { query, page, sortBy } = searchState;
  const newQueryParams = new URLSearchParams();
  if (searchState.syncQueryWithUrl && query) newQueryParams.append('query', query);

  if (searchState.syncFiltersWithUrl) {
    searchState.facetFilters?.forEach((filters) => {
      filters?.forEach((filter) => {
        // do not sync negative filters to url
        if (filter.includes(':-')) return;
        const [filterName, ...values] = filter.split(':');
        const value = values.join(':').replace(/^:+/, '');
        if (filterName) {
          newQueryParams.append(filterName, value);
        }
      });
    });

    if (page != null) newQueryParams.append('page', `${page}`);
    if (sortBy != null) newQueryParams.append('sortBy', `${sortBy}`);
  }

  // keep other non search params
  const allParams = router.location.query;
  Object.keys(allParams)?.forEach((key) => {
    if (!SEARCH_STATE_URL_PARAMS.includes(key) && !SEARCH_FACET_PARAMS_NOT_SUPPORTED_BY_ALGOLIA.includes(key)) {
      newQueryParams.append(key, allParams[key]);
    }
  });

  let newPath = `${router.location.pathname ?? ''}?${newQueryParams.toString()}`;

  // URLSearchParams.append() encodes all spaces with plus (+) sign
  // this is being replaced by the %20 sign to match the canonical url.
  if (newPath && /\+/.test(newPath)) {
    newPath = newPath.replace(/\+/g, '%20');
  }

  updateUrl(router, newPath, shouldReplaceRoute);
}

function removeEmptyOrDuplicateFacetFilters(filters: string[][]): string[][] {
  // Remove duplicates and empty strings within each filter group
  // this is a pure function where results are deterministic
  const deduplicatedFilters = filters
    .map((filterGroup: string[]) => {
      const uniqueNonEmptyFilters = [...new Set(filterGroup)].filter((filter) => filter !== '');
      uniqueNonEmptyFilters.sort(); // Sort filters within each group
      return uniqueNonEmptyFilters;
    })
    .filter((filterGroup: string[]) => filterGroup.length > 0) // Remove empty arrays
    .sort((a: string[], b: string[]) => {
      const firstA = a[0];
      const firstB = b[0];
      if (firstA && firstB) {
        return firstA.localeCompare(firstB);
      }
      return 0;
    }) // Sort filter groups based on the first element of each group
    .map((group) => JSON.stringify(group)) // Convert each group to a JSON string for comparison
    .filter((group, index, self) => self.indexOf(group) === index) // Remove duplicate groups
    .map((group) => JSON.parse(group)); // Convert back to arrays

  // Deduplicate entire filter groups by converting them to JSON strings, using a Set, and then parsing them back
  const uniqueFilterGroups = Array.from(
    new Set(deduplicatedFilters.map((filterGroup) => JSON.stringify(filterGroup)))
  ).map((jsonString) => JSON.parse(jsonString) as string[]);

  return uniqueFilterGroups;
}

function combineFacetFilters(configFilters: string[][] = [], paramFilters: string[][] = []): string[][] {
  const combinedFacetFilters = JSON.parse(JSON.stringify(configFilters));
  const paramFiltersCopy = JSON.parse(JSON.stringify(paramFilters)); // Deep copy

  paramFiltersCopy.forEach((paramFilter: string[]) => {
    if (paramFilter.length > 0) {
      const firstParamFilter = paramFilter[0];
      if (firstParamFilter) {
        const paramFilterKey = firstParamFilter.split(':')[0];
        const isNegativeFilter = firstParamFilter.includes(':-');
        const matchingConfigFilterIndex = combinedFacetFilters.findIndex((configFilter: string[]) => {
          const firstConfigFilter = configFilter[0];
          return (
            configFilter.length > 0 &&
            firstConfigFilter &&
            firstConfigFilter.split(':')[0] === paramFilterKey &&
            firstConfigFilter.includes(':-') === isNegativeFilter
          );
        });

        if (matchingConfigFilterIndex !== -1 && !isNegativeFilter) {
          combinedFacetFilters[matchingConfigFilterIndex].push(...paramFilter);
        } else {
          combinedFacetFilters.push(paramFilter);
        }
      }
    }
  });

  return removeEmptyOrDuplicateFacetFilters(combinedFacetFilters);
}

function sortFilterGroup(filterGroup: string[]) {
  return filterGroup.sort();
}

function combineConfigWithUrlParam(searchConfigs: SearchConfig[], params: SearchURLParameters): SearchState[] {
  return searchConfigs.map((config) => {
    let newConfig: SearchState = { ...config, maxValuesPerFacet: config.maxValuesPerFacet || 1000 };
    if (config.syncQueryWithUrl) {
      newConfig.query = params.query;
    } else if (config.query) {
      newConfig.query = config.query;
    }
    if (config.syncFiltersWithUrl) {
      // Filter out negative filters from URL params only (not from config)
      const filteredParamFilters =
        params.facetFilters
          ?.map((filterGroup) => filterGroup.filter((filter) => !filter.includes(':-')))
          .filter((filterGroup) => filterGroup.length > 0) || [];

      const combinedFacetFilters = combineFacetFilters(config.facetFilters, filteredParamFilters);

      // Ensure sorting is still correct after filter operation
      const sortedFilteredCombinedFilters = combinedFacetFilters.map(sortFilterGroup);

      newConfig = {
        ...config,
        ...params,
        maxValuesPerFacet: config.maxValuesPerFacet || 1000,
        facetFilters: sortedFilteredCombinedFilters,
      };
    }
    newConfig.facetFilters = newConfig.facetFilters?.map((filterGroup) => filterGroup.sort());
    return newConfig;
  });
}

function addFiltersToFacetFilters(filters: string[], facetFilters?: string[][]): string[][] {
  // clone array so useSearchQuery refetch
  const newFacetFilters = facetFilters ? [...facetFilters] : [];

  // Map facet names to their index in the newFacetFilters array
  const facetToIndex: Record<string, number> = {};
  newFacetFilters.forEach((facet: string[], index: number) => {
    // and remove filters that excludes because it won't work without the separation
    if (facet?.[0]?.includes(':-')) return;
    const firstFacet = facet?.[0];
    if (firstFacet) {
      const facetKey = firstFacet.split(':')[0];
      if (facetKey) {
        facetToIndex[facetKey] = index;
      }
    }
  });

  // Add filters to corresponding facet or create a new facet
  filters.forEach((filter) => {
    const colonIndex = filter.indexOf(':');
    const facetName = filter.slice(0, colonIndex);
    const facetValue = filter.slice(colonIndex + 1);
    const filterToAdd = `${facetName}:${facetValue}`;

    if (facetToIndex[facetName] !== undefined) {
      // If facet exists, check if the filter is already present in it
      const facetIndex = facetToIndex[facetName];
      const existingFilters = newFacetFilters[facetIndex];
      if (existingFilters && existingFilters.indexOf(filterToAdd) === -1) {
        // making sure to deep clone so useSearchQuery refetch
        newFacetFilters[facetIndex] = [...existingFilters, filterToAdd];
      }
    } else {
      // If facet does not exist, create a new one
      newFacetFilters.push([filterToAdd]);
      facetToIndex[facetName] = newFacetFilters.length - 1;
    }
  });

  return newFacetFilters;
}

function removeFiltersFromFacetFilters(filters: string[], facetFilters: string[][]): string[][] {
  return facetFilters.map((fs) => fs.filter((f) => !filters.includes(f))).filter((fs) => fs.length > 0);
}

function setFiltersForFacet(facet: string, filters: string[], facetFilters?: string[][]): string[][] {
  // clone array so useSearchQuery refetch
  const newFacetFilters = facetFilters ? [...facetFilters] : [];

  // find the index of the filter[] to be replaced, skipping negative exclusion groups (e.g.
  // productTypeDescription:-Degrees) so they are never overwritten by a positive selection
  const indexOfToBeReplacedFilters = newFacetFilters.findIndex(
    (_facet) => _facet[0]?.startsWith(facet) && !_facet[0]?.includes(':-')
  );

  // replace or push filter[]
  if (indexOfToBeReplacedFilters > -1) {
    newFacetFilters[indexOfToBeReplacedFilters] = filters;
  } else {
    newFacetFilters.push(filters);
  }

  return newFacetFilters;
}

// Filters out any hidden filters (SEARCH-2407) from a list of filters a caller is trying to
// remove, so clearFilters/removeFilters can never drop a search state's configured hidden filters.
function excludeHiddenFilters(filters: string[], hiddenFilters?: string[]): string[] {
  return hiddenFilters?.length ? filters.filter((f) => !hiddenFilters.includes(f)) : filters;
}

function clearFacetFilters(facetFilters?: string[][], hiddenFilters?: string[]): string[][] {
  // clone array so useSearchQuery refetch
  const newFacetFilters: string[][] = [];
  facetFilters?.forEach((filters) => {
    // Coursera Plus survives "Clear all" on every surface; it is treated as non-clearable,
    // matching hasClearableActiveFilters in components/filters/utils.ts. Negative/exclusion
    // filters (e.g. productTypeDescription:-Degrees) are config-level defaults, not user-added
    // filters, so they survive too - consistent with how they're protected everywhere else in this
    // file (combineConfigWithUrlParam, addFiltersToFacetFilters, setFiltersForFacet). Configured
    // hiddenFilters (SEARCH-2407) survive for the same reason.
    const filtered = filters.filter(
      (item) => item.includes(':-') || item.includes(IS_PART_OF_COURSERA_PLUS) || hiddenFilters?.includes(item)
    );
    if (filtered && filtered.length > 0) newFacetFilters.push(filtered);
  });

  return newFacetFilters;
}

const mapStateToSearchRequest = (searchState: SearchState): SearchRequest => {
  const { page, query, id: _id, ...rest } = searchState;
  const SearchStateCopy = { ...rest };
  // intentionally filtering the FE defined variables (including id) to prevents API error
  delete SearchStateCopy.syncQueryWithUrl;
  delete SearchStateCopy.syncFiltersWithUrl;
  delete SearchStateCopy.disableFacetFilterHack;
  // hiddenFilters (SEARCH-2407) is FE-only bookkeeping for SearchProvider's clearFilters/
  // removeFilters guard - the BE schema has no such field, only facetFilters.
  delete SearchStateCopy.hiddenFilters;
  const facetFilters = removeEmptyOrDuplicateFacetFilters(searchState.facetFilters ?? []) || [];

  // Check session storage for explain state if not explicitly set
  let explainValue = searchState.explain;
  if (explainValue === undefined && typeof window !== 'undefined') {
    const sessionExplain = sessionStorage.getItem('enableSearchExplain');
    explainValue = sessionExplain === 'true';
  }

  return {
    ...SearchStateCopy,
    cursor: page ? `${page - 1}` : '0',
    query: query || '',
    facetFilters,
    // Include explain parameter only when explicitly true
    ...(explainValue === true && { explain: explainValue }),
  };
};

export function getAugmentedProperties(rawResult: SearchResultRaw): SearchResultAugments {
  const hitsDisplayedCount = rawResult.elements?.length || 0;
  const hitsTotalCount = rawResult.pagination?.totalElements || 0;
  const inferredCurrentPageNumber = hitsDisplayedCount ? Math.ceil(hitsDisplayedCount / NUMBER_OF_RESULTS_PER_PAGE) : 1;
  return { hitsDisplayedCount, hitsTotalCount, inferredCurrentPageNumber };
}

const combineResultsWithStates = (
  searchStates: SearchState[],
  results?: SearchResultRaw[]
): SearchResults | undefined => {
  return results?.map((rawResult, index) => {
    const searchState = searchStates[index];
    if (!searchState) {
      throw new Error(`No search state found for index ${index}`);
    }
    return {
      ...searchState,
      ...rawResult,
      facets: rawResult.facets,
      ...getAugmentedProperties(rawResult),
    };
  });
};

function removeNegativeFilters(results: SearchResults | undefined, searchConfigs: SearchConfig[]) {
  return results?.map((result, index) => {
    const config = searchConfigs[index];
    const negativeFilters = config?.facetFilters
      ?.flat()
      .filter((filter) => filter.includes(':-'))
      .map((filter) => filter.split(':-')[1]);

    // Remove negative filters from facetFilters that are shown to UI components
    const filteredFacetFilters = result.facetFilters
      ?.map((filterGroup) => filterGroup.filter((filter) => !filter.includes(':-')))
      .filter((filterGroup) => filterGroup.length > 0);

    // Remove negative filter options from facets (existing logic)
    let newFacets = result.facets;
    if (result?.facets && negativeFilters && negativeFilters.length > 0) {
      newFacets = result.facets.map((facet) => {
        if (facet?.valuesAndCounts) {
          const newValuesAndCounts = facet.valuesAndCounts.filter(
            (valueAndCount) => !negativeFilters.includes(valueAndCount.value)
          );
          return { ...facet, valuesAndCounts: newValuesAndCounts };
        }
        return facet;
      });
    }

    return {
      ...result,
      facetFilters: filteredFacetFilters, // Remove negative filters from state shown to UI
      facets: newFacets,
    };
  });
}

/**
 * Combines existing search states with the results to include local only data
 */
const mergeStateWithResults = (
  data: SearchQuery | undefined,
  searchStatesArray: SearchState[],
  searchConfigs: SearchConfig[]
) => {
  const results = combineResultsWithStates(
    searchStatesArray,
    data?.SearchResult?.search?.slice(0, searchStatesArray.length)
  );

  // remove negative filters facets from search UI
  return removeNegativeFilters(results, searchConfigs);
};

const getSearchRequestsFromSearchStates = (searchStatesArray: SearchState[]) => {
  const searchRequests: SearchRequests = searchStatesArray?.map(mapStateToSearchRequest);
  // Create a copy of the searchRequests
  return [...searchRequests];
};

const getProviderFetchMoreUpdateQuery =
  (indexToLoadMoreOn: number) =>
  (prev: SearchQuery, { fetchMoreResult }: { fetchMoreResult: SearchQuery }) => {
    // if there's nothing more to fetch, just return prev
    if (!fetchMoreResult) return prev;

    const combinedResults: SearchQuery = cloneDeep(prev);
    const previousPageResults = prev?.SearchResult?.search?.[indexToLoadMoreOn]?.elements || [];
    const nextPageResults = fetchMoreResult?.SearchResult?.search?.[indexToLoadMoreOn]?.elements || [];

    // combine the results if existing results are not null or undefined
    if (
      combinedResults?.SearchResult?.search?.[indexToLoadMoreOn] !== undefined &&
      combinedResults?.SearchResult?.search?.[indexToLoadMoreOn] !== null
    ) {
      const combinedElements = {
        ...combinedResults.SearchResult.search[indexToLoadMoreOn],
        elements: [...previousPageResults, ...nextPageResults],
      };
      combinedResults.SearchResult.search[indexToLoadMoreOn] = combinedElements;
    }
    return combinedResults;
  };

export {
  updateUrl,
  filtersToKeyValuesMap,
  syncSearchStateToUrl,
  combineConfigWithUrlParam,
  addFiltersToFacetFilters,
  removeFiltersFromFacetFilters,
  excludeHiddenFilters,
  clearFacetFilters,
  setFiltersForFacet,
  mapStateToSearchRequest,
  combineResultsWithStates,
  combineFacetFilters,
  removeNegativeFilters,
  mergeStateWithResults,
  removeEmptyOrDuplicateFacetFilters,
  getSearchRequestsFromSearchStates,
  getProviderFetchMoreUpdateQuery,
};
